import React from 'react';
import classNames from 'classnames';
import s from './ProductList.scss';
import {IProvidedGlobalProps, withGlobalProps} from '../providers/GlobalPropsProvider';
import {ProductItem} from '../ProductItem/ProductItem';
import {LoadMoreButton} from './LoadMoreButton/LoadMoreButton';
import {EmptyGallery} from './EmptyGallery/EmptyGallery';
import autobind from 'autobind-decorator';
import {DataHook as ProductImagesDataHook} from '../ProductItem/ProductImage/ProductImage';
import imagesLoaded from 'imagesloaded';

export interface ProductListProps extends IProvidedGlobalProps {
  hasFilters: boolean;
}

interface ProductListState {
  inBrowser: boolean;
}

@withGlobalProps
@autobind
export class ProductList extends React.Component<ProductListProps, ProductListState> {
  private readonly productItemRefs = [];
  private readonly maxProductsPerPage: number = this.getMaxProductsPerPage();
  private imagesLoaded = false;
  public state = {inBrowser: false};
  private isInteractive = false;

  public componentDidMount(): void {
    this.setState({inBrowser: true}, () => {
      imagesLoaded(document.querySelectorAll(`[data-hook="${ProductImagesDataHook.Images}"]`), () => {
        this.imagesLoaded = true;
        this.reportLoad();
      });
    });
  }

  private reportLoad() {
    if (this.props.globals.isInteractive && this.imagesLoaded) {
      this.props.globals.appLoadBI.loaded();
    }
  }

  public componentDidUpdate(prevProps: IProvidedGlobalProps) {
    if (!this.isInteractive && this.props.globals.isInteractive) {
      this.isInteractive = true;
      /* istanbul ignore next: hard to test it */
      this.props.globals.updateLayout && this.props.globals.updateLayout();
      this.reportLoad();
    }

    if (this.shouldFocusOnNewlyDisplayedProduct(prevProps)) {
      this.productItemRefs[this.props.globals.focusedProductIndex].focus();
    }
  }

  public render() {
    const {products} = this.props.globals;
    return (
      <section data-hook="product-list" aria-label={this.props.globals.textsMap.galleryRegionSR}>
        {products.length === 0 ? this.getEmptyList() : this.getProductList()}
      </section>
    );
  }

  private getMaxProductsPerPage(): number {
    if (this.props.globals.isMobile) {
      return 10;
    }

    return this.props.globals.styleParams.numbers.galleryColumns * this.props.globals.styleParams.numbers.galleryRows;
  }

  private getEmptyList() {
    return <EmptyGallery hasFilters={this.props.hasFilters} />;
  }

  private shouldFocusOnNewlyDisplayedProduct(prevProps) {
    return (
      !this.props.globals.isFirstPage &&
      this.props.globals.focusedProductIndex !== prevProps.globals.focusedProductIndex
    );
  }

  private getNumberOfVisibleProducts(): number {
    return this.props.globals.isFirstPage ? this.maxProductsPerPage : this.props.globals.products.length;
  }

  private getProductList() {
    const {products} = this.props.globals;
    return (
      <>
        <ul
          className={classNames(s.productsGrid, {[s.isMobile]: this.props.globals.isMobile})}
          data-hook="product-list-wrapper">
          {products.slice(0, this.getNumberOfVisibleProducts()).map((product, index) => {
            return (
              <li key={product.id}>
                <ProductItem index={index} product={product} innerRef={c => (this.productItemRefs[index] = c)} />
              </li>
            );
          })}
        </ul>
        {this.shouldShowLoadMoreButton() && this.getLoadMoreButton()}
      </>
    );
  }

  private shouldShowLoadMoreButton(): boolean {
    const {
      globals: {isFirstPage, hasMoreProductsToLoad, totalProducts},
    } = this.props;
    if (isFirstPage) {
      return this.maxProductsPerPage < totalProducts;
    }
    return hasMoreProductsToLoad;
  }

  private getLoadMoreButton() {
    return <LoadMoreButton loadMoreClicked={this.loadMoreClicked} />;
  }

  private async loadMoreClicked() {
    this.props.globals.setProductsPerPage(this.maxProductsPerPage);
    await this.props.globals.loadMoreProducts(this.getNumberOfVisibleProducts());
  }
}
